/* eslint-disable camelcase */
import { CareerSiteOpening, OpenAPI } from 'api-clients/monolith';
import { AppContext } from 'contexts/app-context';
import { FeatureFlagContext } from 'contexts/feature-flag-context';
import { BriefcaseIcon } from 'icons/briefcase';
import { CaretRight } from 'icons/caret-right';
import { CurrencyIcon } from 'icons/currency';
import { LocationMarkerIcon } from 'icons/location-marker';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { RouteParams } from 'types';

import { Skeleton } from 'components/ui/skeleton';

interface JobsListProps {
  jobs: CareerSiteOpening[];
}

export function JobsList({ jobs }: JobsListProps) {
  const { accountSlug } = useParams<RouteParams>();
  const { isMobile } = useContext(AppContext);
  const {
    featureFlags: { careerSiteInterdependentFilters },
  } = useContext(FeatureFlagContext);

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const utmSource = params.get('utm_source') ?? undefined;
  const utmCampaign = params.get('utm_campaign') ?? undefined;
  const utmMedium = params.get('utm_medium') ?? undefined;
  const utmTerm = params.get('utm_term') ?? undefined;
  const utmContent = params.get('utm_content') ?? undefined;
  const referralWorkerName = params.get('referralWorkerName') ?? undefined;
  const shortCodeApplicantId =
    params.get('short_code_applicant_id') ?? undefined;
  const keywordId = params.get('keyword_id') ?? undefined;

  const handleApplyClick = (id: string) => {
    // Get updated URL params from the location hook
    const localeParam = params.get('locale') ?? undefined;

    const url = new URL(`${OpenAPI.BASE}/${accountSlug}/apply/${id}`);
    url.searchParams.set('utm_medium', 'ai-career-site');
    if (utmSource) url.searchParams.set('utm_source', utmSource);
    if (utmCampaign) url.searchParams.set('utm_campaign', utmCampaign);
    if (utmMedium) url.searchParams.set('utm_medium', utmMedium);
    if (utmTerm) url.searchParams.set('utm_term', utmTerm);
    if (utmContent) url.searchParams.set('utm_content', utmContent);
    if (referralWorkerName)
      url.searchParams.set('referralWorkerName', referralWorkerName);
    if (localeParam) url.searchParams.set('locale', localeParam);
    if (shortCodeApplicantId)
      url.searchParams.set('short_code_applicant_id', shortCodeApplicantId);
    if (keywordId) url.searchParams.set('keyword_id', keywordId);

    window.open(url, '_blank');
  };

  if (!jobs || jobs.length === 0) {
    return null;
  }

  return (
    <>
      {jobs.map(job => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <button
          key={job.id}
          className="flex items-center justify-between rounded-[12px] border border-gray-200 p-3 shadow-sm hover:cursor-pointer md:border-b md:border-none md:border-b-gray-200 md:px-10 md:py-4 md:hover:cursor-default"
          type="button"
          onClick={() => isMobile && handleApplyClick(job.id)}
        >
          <div className="flex flex-col items-start gap-2">
            <h3 className="text-16 font-500 whitespace-normal text-start leading-6">
              {job.title}
            </h3>
            <div className="text-14 flex flex-col items-start gap-[2px] leading-5 text-gray-600 md:flex-row md:items-center md:gap-4">
              {job.location && (
                <div className="flex flex-row items-center gap-1">
                  <LocationMarkerIcon className="hidden md:block" />
                  <p>{job.location}</p>
                  {job.distance && (
                    <>
                      <span className="h-1 w-1 rounded-full bg-gray-600"></span>
                      <p>{job.distance}</p>
                    </>
                  )}
                </div>
              )}
              {job.pay_rate && (
                <div className="flex flex-row items-center gap-1">
                  <CurrencyIcon className="hidden md:block" />
                  <p>{job.pay_rate}</p>
                </div>
              )}
              {job.job_type && (
                <div className="flex flex-row items-center gap-1">
                  <BriefcaseIcon className="hidden md:block" />
                  <p>{job.job_type}</p>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => handleApplyClick(job.id)}
            type="button"
            className={
              careerSiteInterdependentFilters
                ? 'border-primary-600 text-primary-600 hover:text-primary-700 hidden h-10 items-center rounded-full border px-6 text-base font-medium leading-6 md:flex'
                : 'bg-primary text-primary-foreground hover:bg-primary-600 text-16 font-500 rounded-6 hidden h-[40px] items-center gap-4 px-6 py-1 leading-5 shadow-sm md:flex'
            }
          >
            <FormattedMessage
              defaultMessage="Apply"
              description="Apply to position button label"
            />
            <CaretRight />
          </button>
        </button>
      ))}
    </>
  );
}

JobsList.Skeleton = function JobsListSkeleton() {
  return (
    <div className="flex items-center justify-between rounded-[12px] border border-gray-200 p-3 shadow-sm md:border-b md:border-none md:border-b-gray-200 md:px-10 md:py-4">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-[200px]" />
        <div className="text-14 flex flex-col items-start gap-[2px] leading-5 text-gray-600 md:flex-row md:items-center md:gap-4">
          <Skeleton className="h-5 w-[150px]" />
          <Skeleton className="h-5 w-[150px]" />
          <Skeleton className="h-5 w-[150px]" />
        </div>
      </div>
      <Skeleton className="hidden h-10 w-[125px] md:block" />
    </div>
  );
};

JobsList.SkeletonGroup = function JobsListSkeletonGroup() {
  return (
    <>
      {Array.from({ length: 10 }, (_, i) => (
        <JobsList.Skeleton key={`skeleton-${i}`} />
      ))}
    </>
  );
};
